// @ts-ignore
import * as Sentry from "@sentry/capacitor";

/** Longest edge an uploaded photo may have, matching the previous plugin limit. */
const MAX_EDGE = 1000;

/** JPEG quality for the re-encoded upload, matching the previous plugin quality. */
const JPEG_QUALITY = 0.7;

export interface PreparedPhoto {
    /** The photo to upload, re-encoded and scaled down where that succeeded. */
    file: File;
    /** False when re-encoding failed and the original is being uploaded as-is. */
    wasReEncoded: boolean;
}

/**
 * Prepares photos coming out of the platform's camera app for upload.
 *
 * Photos captured through the system camera arrive untouched: full sensor
 * resolution, and on iOS possibly HEIC. Re-encoding through a canvas fixes both
 * at once, because the canvas always writes JPEG regardless of what went in.
 *
 * See docs/photos.md.
 */
export default {
    /**
     * Scales the photo so its longest edge is at most MAX_EDGE and re-encodes it
     * to JPEG. Falls back to the original file when the browser cannot decode it,
     * so a capture is never lost to a re-encoding problem.
     * @param photo
     */
    async prepareForUpload(photo: File): Promise<PreparedPhoto> {
        try {
            // `from-image` makes the browser apply the EXIF rotation that phone
            // cameras rely on, instead of handing back a sideways bitmap. The cast
            // is needed because this TypeScript's DOM typings predate the value.
            const bitmap = await createImageBitmap(
                photo,
                {imageOrientation: "from-image"} as unknown as ImageBitmapOptions
            );

            try {
                const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));

                const canvas = document.createElement("canvas");
                canvas.width = Math.round(bitmap.width * scale);
                canvas.height = Math.round(bitmap.height * scale);

                const context = canvas.getContext("2d");

                if (!context) {
                    throw new Error("2d canvas context unavailable");
                }

                context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

                const blob = await new Promise<Blob | null>((resolve) => {
                    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY);
                });

                if (!blob) {
                    throw new Error("canvas produced no blob");
                }

                return {
                    file: new File([blob], "photo.jpg", {type: "image/jpeg"}),
                    wasReEncoded: true,
                };
            } finally {
                // A bitmap holds native memory until closed, also on failure.
                bitmap.close();
            }
        } catch (error) {
            // Worth knowing about: it means monteurs are uploading originals,
            // which are large and may not even be JPEG.
            Sentry.captureMessage("Photo re-encoding failed, uploading original", {
                level: "warning",
                contexts: {
                    photo: {
                        type: photo.type,
                        bytes: photo.size,
                        reason: error instanceof Error ? error.message : String(error),
                        userAgent: navigator.userAgent,
                    },
                },
            });

            return {file: photo, wasReEncoded: false};
        }
    },

    /**
     * Reports a capture that went wrong, with the device attached.
     * @param error
     */
    reportCaptureFailure(error: unknown): void {
        Sentry.captureException(error, {
            contexts: {
                photo_capture: {
                    userAgent: navigator.userAgent,
                    isSecureContext: window.isSecureContext === true,
                },
            },
        });
    },
};
